import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { apiService } from '../services/api';
import { User, Employee, LoginCredentials, RegisterData } from '../types';

interface AuthContextData {
  user: User | null;
  employee: Employee | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginCredentials) => Promise<void>;
  employeeLogin: (credentials: LoginCredentials) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => void;
  isOwner: boolean;
  isEmployee: boolean;
  isAdmin: boolean;
  isSuperAdmin: boolean;
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [employee, setEmployee] = useState<Employee | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const isAuthenticated = !!(user || employee);
  const isOwner = !!user && (!user.role || user.role === 'user');
  const isEmployee = !!employee;
  const isAdmin = !!user && user.role === 'admin';
  const isSuperAdmin = !!user && user.role === 'super_admin';

  useEffect(() => {
    const loadStoredAuth = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const storedUser = localStorage.getItem('auth_user');

        if (token && storedUser) {
          const userData = JSON.parse(storedUser);
          
          if (userData.user) {
            setUser(userData.user);
          } else if (userData.employee) {
            setEmployee(userData.employee);
          }

          // Verificar se o token ainda é válido
          try {
            const response = await apiService.me();
            if (response.success) {
              // Verificar se é employee (tem is_active definido)
              if (response.data.is_active !== undefined) {
                // É um employee
                setEmployee(response.data);
                setUser(null);
              } else {
                // É um user (dono de loja ou admin)
                setUser(response.data);
                setEmployee(null);
              }
            }
          } catch (error) {
            // Token inválido, limpar dados
            localStorage.removeItem('auth_token');
            localStorage.removeItem('auth_user');
            setUser(null);
            setEmployee(null);
          }
        }
      } catch (error) {
        console.error('Erro ao carregar dados de autenticação:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadStoredAuth();
  }, []);

  const login = async (credentials: LoginCredentials): Promise<void> => {
    try {
      const response = await apiService.login(credentials);

      if (response.success && response.data) {
        const { access_token, user: userData } = response.data;

        localStorage.setItem('auth_token', access_token);
        localStorage.setItem('auth_user', JSON.stringify({ user: userData }));

        setUser(userData || null);
        setEmployee(null);
      } else {
        throw new Error(response.message || 'Erro no login');
      }
    } catch (error: any) {
      // Tratar erros de validação específicos
      if (error.response?.data?.errors) {
        const errors = error.response.data.errors;
        const firstError = Object.values(errors)[0];
        if (Array.isArray(firstError) && firstError[0]) {
          throw new Error(firstError[0] as string);
        }
      }
      throw new Error(error.response?.data?.message || 'Erro no login');
    }
  };

  const employeeLogin = async (credentials: LoginCredentials): Promise<void> => {
    try {
      const response = await apiService.employeeLogin(credentials);

      if (response.success && response.data) {
        const { access_token, employee: employeeData } = response.data;

        localStorage.setItem('auth_token', access_token);
        localStorage.setItem('auth_user', JSON.stringify({ employee: employeeData }));

        setEmployee(employeeData || null);
        setUser(null);
      } else {
        throw new Error(response.message || 'Erro no login');
      }
    } catch (error: any) {
      // Tratar erros de validação específicos
      if (error.response?.data?.errors) {
        const errors = error.response.data.errors;
        const firstError = Object.values(errors)[0];
        if (Array.isArray(firstError) && firstError[0]) {
          throw new Error(firstError[0] as string);
        }
      }
      throw new Error(error.response?.data?.message || 'Erro no login do funcionário');
    }
  };

  const register = async (data: RegisterData): Promise<void> => {
    try {
      const response = await apiService.register(data);

      if (response.success && response.data) {
        const { access_token, user: userData } = response.data;

        localStorage.setItem('auth_token', access_token);
        localStorage.setItem('auth_user', JSON.stringify({ user: userData }));

        setUser(userData || null);
        setEmployee(null);
      } else {
        throw new Error(response.message || 'Erro no registro');
      }
    } catch (error: any) {
      // Tratar erros de validação específicos
      if (error.response?.data?.errors) {
        const errors = error.response.data.errors;
        if (errors.email && errors.email[0]) {
          throw new Error(errors.email[0]);
        } else if (errors.cpf && errors.cpf[0]) {
          throw new Error(errors.cpf[0]);
        } else {
          // Pegar o primeiro erro disponível
          const firstError = Object.values(errors)[0];
          if (Array.isArray(firstError) && firstError[0]) {
            throw new Error(firstError[0] as string);
          }
        }
      }
      throw new Error(error.response?.data?.message || 'Erro no registro');
    }
  };

  const logout = async (): Promise<void> => {
    try {
      await apiService.logout();
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
    } finally {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_user');
      setUser(null);
      setEmployee(null);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        employee,
        isAuthenticated,
        isLoading,
        login,
        employeeLogin,
        register,
        logout,
        isOwner,
        isEmployee,
        isAdmin,
        isSuperAdmin,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextData => {
  const context = useContext(AuthContext);
  
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  
  return context;
};
